import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "../../components/ThemeContext";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Building2, ChevronDown, CheckCircle, Shield, User, Circle, X, Smartphone, ArrowLeft, RefreshCw, KeyRound } from "lucide-react";
import AxonMetricHeader from "../../components/layout/AxonMetricHeader.jsx";
import { useAuth } from "../../context/AuthContext.tsx";
import apiService from "../../services/api";
import backendApi from "../../services/backendApi";
import { storeUserAvatar } from "../../utils/userAvatar";
import { useSiteSSE } from "../../hooks/useSiteSSE";

// Site data will be loaded from API

// Enhanced Floating Time and Date Widget
const FloatingTimeDate = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-20 right-6 z-30 bg-gradient-to-br from-[#0084CF] via-[#0066A3] to-[#0066A3] backdrop-blur-md border border-white/20 rounded-xl shadow-xl shadow-[#0084CF]/30 px-4 py-3"
    >
      <div className="text-right text-white">
        <div className="text-sm font-bold">
          {time.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}
        </div>
        <div className="text-xs font-medium opacity-90">
          {time.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          })}
        </div>
      </div>
    </motion.div>
  );
};

const BestLoginPage = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();
  // Credentials state
  const [credentials, setCredentials] = useState({ username: "", password: "" });

  // UI State
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // MFA / Microsoft Authenticator state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Initial fetch only (no polling)
  // Initial check for remembered company code - REMOVED


  // Load remembered credentials on component mount
  useEffect(() => {
    const rememberedSite = localStorage.getItem('rememberedSite');
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    localStorage.removeItem('rememberedPassword');

    if (rememberedSite && rememberedUsername) {
      setCredentials({
        selectedSite: rememberedSite,
        username: rememberedUsername,
        password: ""
      });
      setRememberMe(true);
    }
  }, []);

  // Post-login completion helper (used by normal login and MFA verification)
  const completeLoginSuccess = (response) => {
    const responseRoleName = response?.user?.role_name || response?.data?.user?.role_name || response?.user?.role || null;

    // Aggressive site code resolution helper
    const findSiteCode = (obj) => {
      if (!obj) return null;
      if (obj.site_code) return obj.site_code;
      if (obj.siteCode) return obj.siteCode;
      if (obj.current_site?.site_code) return obj.current_site.site_code;
      if (obj.site?.site_code) return obj.site.site_code;
      if (obj.user?.site_code) return obj.user.site_code;
      if (obj.data?.current_site?.site_code) return obj.data.current_site.site_code;
      if (obj.data?.user?.site_code) return obj.data.user.site_code;
      return null;
    };

    const isHoAdmin =
      responseRoleName === 'ho_admin' ||
      (
        (response?.user?.role === 'admin' || responseRoleName === 'admin') &&
        !response?.user?.is_super_admin &&
        !response?.user?.is_superuser &&
        !!(response?.data?.company?.company_id || response?.user?.company_id) &&
        !findSiteCode(response)
      );

    const isSystemAdmin =
      !!(response?.user?.is_super_admin || response?.user?.is_superuser) ||
      responseRoleName === 'system_admin' ||
      responseRoleName === 'super_admin';

    // Resolve the actual site code
    const resolvedSiteCode = findSiteCode(response) || findSiteCode(response.data) || findSiteCode(response.user);
    const availableSiteCode = response?.data?.available_sites?.find(site => site.is_primary)?.site_code
      || response?.data?.available_sites?.[0]?.site_code;
    const rememberedSiteCode = localStorage.getItem('currentSite');

    const userSite = (
      resolvedSiteCode ||
      availableSiteCode ||
      rememberedSiteCode ||
      ''
    ).toUpperCase();

    // Create user object for AuthContext
    const userData = {
      username: response.user.username,
      fullName: [response.user.first_name, response.user.last_name].filter(Boolean).join(' ') || response.user.username,
      role: response.user.role,
      roleName: responseRoleName,
      token: response.token,
      isGlobalAdmin: !!(response.user.is_super_admin || response.user.is_superuser),
      tenantName: response.user.tenant_name || response?.data?.current_site?.site_name || 'AxonMetric',
      tenant: userSite,
      siteCode: userSite,
      siteId: response?.data?.current_site?.site_id || response?.user?.site_id || null,
      companyId: response?.data?.company?.company_id || response?.user?.company_id || null,
      userId: response?.user?.user_id || response?.user?.id || null,
      email: response.user.email,
      avatar: response.user.avatar || response.user.profile_picture || null
    };

    // Store avatar in user-specific localStorage if available
    if (userData.avatar && userData.username) {
      storeUserAvatar(userData.avatar, userData.username);
    }

    // Use the AuthContext login function
    login(userData);

    // Handle remember me functionality
    if (rememberMe) {
      localStorage.setItem('rememberedSite', userSite || credentials.selectedSite || '');
      localStorage.setItem('rememberedUsername', credentials.username);
    } else {
      localStorage.removeItem('rememberedSite');
      localStorage.removeItem('rememberedUsername');
    }
    localStorage.removeItem('rememberedPassword');

    if (userSite) {
      localStorage.setItem('currentSite', userSite);
    } else {
      localStorage.removeItem('currentSite');
    }
    localStorage.setItem('currentUsername', credentials.username);

    // Navigate based on role
    setTimeout(() => {
      if (isSystemAdmin) {
        navigate('/select-company', { replace: true });
      } else if (isHoAdmin) {
        navigate('/select-site', { replace: true });
      } else {
        navigate(userSite ? `/dashboard/${userSite}` : '/select-site', { replace: true });
      }
    }, 500);
  };

  const handleVerifyMFA = async (e) => {
    if (e) e.preventDefault();
    const cleanCode = mfaCode.trim().replace(/\s+/g, '');
    if (!cleanCode) {
      setMfaError("Please enter the verification code.");
      return;
    }
    setMfaLoading(true);
    setMfaError("");

    try {
      const response = await apiService.verifyLoginMFA(mfaToken, cleanCode);
      if (response.success && response.token) {
        completeLoginSuccess(response);
      } else {
        setMfaError(response.error || response.message || "Invalid verification code. Please check Microsoft Authenticator.");
      }
    } catch (err) {
      setMfaError(err.message || "Verification failed. Please check your code.");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Validate required fields
    if (!credentials.username || !credentials.password) {
      setError("Please enter both username and password.");
      setIsLoading(false);
      return;
    }

    try {
      // Use the API service to authenticate with Django backend
      // No site or company code needed upfront
      const response = await apiService.login(
        credentials.username,
        credentials.password,
        null,
        null,
        null
      );

      if (response.success) {
        if (response.mfa_required) {
          setMfaRequired(true);
          setMfaToken(response.mfa_token);
          setMfaCode("");
          setMfaError("");
          setIsLoading(false);
          return;
        }
        completeLoginSuccess(response);
      } else {
        // Handle specific error types from Django backend
        if (response.error === 'ACCOUNT_LOCKED') {
          const minutes = response.lockout_remaining_minutes || 0;
          setError(`Account is temporarily locked due to too many failed login attempts. Please try again in ${minutes} minute(s).`);
        } else if (response.error === 'RATE_LIMIT_EXCEEDED') {
          setError("Too many login attempts. Please wait 15 minutes before trying again.");
        } else if (response.error === 'ACCOUNT_DEACTIVATED') {
          setError("Your account has been deactivated. Please contact support.");
        } else if (response.error === 'SITE_MISMATCH' || response.error === 'NO_SITE_ASSIGNED') {
          // Site authorization errors
          setError(response.message || "Access denied. Please select the correct site for your account.");
        } else if (response.error === 'INVALID_SITE') {
          setError(response.message || "Invalid site selected. Please choose a valid site.");
        } else if (response.error && response.error.includes("Invalid username or password")) {
          const remaining = response.remaining_attempts;
          if (remaining !== undefined && remaining > 0) {
            setError(`Invalid username or password. ${remaining} attempt(s) remaining before account lockout.`);
          } else {
            setError("Invalid username or password. Please check your credentials and try again.");
          }
        } else if (response.error && response.error.includes("Must include username and password")) {
          setError("Please enter both username and password.");
        } else {
          setError(response.message || response.error || "Login failed. Please try again.");
        }
      }
    } catch (error) {
      // Only log actual errors in development (not connection issues which are handled gracefully)
      if (process.env.NODE_ENV === 'development' && !error.message?.includes('SERVER_NOT_AVAILABLE')) {
        console.error('Login error:', error.message || error);
      }

      if (error.message && (error.message.includes("Failed to fetch") || error.message.includes("SERVER_NOT_AVAILABLE") || error.message.includes("connection"))) {
        setError("Unable to connect to Django server. Please ensure it's running on http://localhost:8000");
      } else if (error.message && error.message.includes("NetworkError")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(error.message || "An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={{
      backgroundImage: "url('/assets/login_bg.jpg')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      minHeight: "100vh",
      position: "relative"
    }}>
      {isDarkMode && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(11, 17, 32, 0.50)",
          zIndex: 0,
          pointerEvents: "none"
        }} />
      )}
      {/* Enhanced CSS Styles with Professional Gradients */}
      <style>{`
        .glass-morphism {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .brand-glass {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.25) 0%, 
            rgba(255, 255, 255, 0.15) 50%, 
            rgba(0, 132, 207, 0.1) 100%
          );
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .floating-animation {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .gradient-border {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 25%, 
            #1C4D88 50%, 
            #0084CF 75%, 
            #0084CF 100%
          );
          background-size: 300% 300%;
          animation: gradientShift 8s ease infinite;
          padding: 2px;
          border-radius: 20px;
          backdrop-filter: blur(20px);
          box-shadow: 0 25px 50px -12px rgba(0, 132, 207, 0.25);
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .inner-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(30px);
          border-radius: 18px;
        }

        .background-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-image: url('/assets/login_bg.jpg');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          z-index: -2;
        }

        .background-overlay {
          display: none;
        }
        
        @keyframes overlayShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .remember-checkbox {
          appearance: none;
          width: 18px;
          height: 18px;
          border: 2px solid #0084CF;
          border-radius: 4px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .remember-checkbox:checked {
          background: linear-gradient(135deg, #0084CF, #0084CF);
          border-color: #0084CF;
        }

        .remember-checkbox:checked::after {
          content: '✓';
          position: absolute;
          top: -1px;
          left: 2px;
          color: white;
          font-size: 12px;
          font-weight: bold;
        }

        .professional-button {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 50%, 
            #1C4D88 100%
          );
          transition: all 0.3s ease;
        }

        .professional-button:hover {
          background: linear-gradient(135deg, 
            #0066A3 0%, 
            #0066A3 50%, 
            #68d391 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(0, 132, 207, 0.4);
        }

        /* Increase font weight for input placeholders */
        input::placeholder {
          font-weight: 400 !important;
        }
      `}</style>

      {/* Keep Original Background Image */}
      <div className="background-container"></div>
      <div className="background-overlay"></div>

      <div className="min-h-screen w-full flex flex-col relative" style={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <AxonMetricHeader />

        {/* Main Content - Adjusted for proper spacing */}
        <main className="relative z-10 flex-1 w-full flex items-center justify-center px-4 py-6" style={{ minHeight: 'calc(100vh - 50px)', paddingTop: '80px' }}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full max-w-5xl mx-auto"
          >
            <div className="gradient-border">
              <div className="inner-content flex rounded-2xl overflow-hidden shadow-2xl">

                {/* Left Panel - Enhanced with Professional Gradients */}
                <section className="hidden md:flex flex-col justify-center w-2/5 p-6 relative overflow-hidden">
                  {/* Beautiful gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F2F8FC]/90 via-[#F2F8FC]/80 to-[#F2F8FC]/70"></div>

                  {/* Enhanced background elements with gradients */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#0084CF]/20 via-[#3EA3DA]/15 to-[#3EA3DA]/10 rounded-full blur-2xl floating-animation"></div>
                  <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-br from-[#C2DFF1]/30 via-[#C2DFF1]/25 to-[#0084CF]/15 rounded-full blur-2xl"></div>

                  <div className="relative z-10">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                      className="mb-8"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-gradient-to-br from-[#0084CF]/20 via-[#C2DFF1]/40 to-[#C2DFF1]/30 rounded-xl shadow-sm border border-[#0084CF]/20 backdrop-blur-sm">
                          <Shield size={24} className="text-[#0084CF]" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-black tracking-wide bg-gradient-to-r from-[#0084CF] via-[#0066A3] to-[#0066A3] bg-clip-text text-transparent" style={{ WebkitTextStroke: '0.5px currentColor' }}>
                            Digitalization of Renewable Assets
                          </h2>
                          <p className="text-[#1C4D88] font-black tracking-wide" style={{ WebkitTextStroke: '0.3px currentColor' }}>Secure Access Portal</p>
                        </div>
                      </div>
                    </motion.div>

                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      className="text-base leading-relaxed mb-8 text-gray-900 font-extrabold"
                    >
                      Advanced real-time analytics platform for comprehensive solar and wind generation monitoring across India's renewable energy infrastructure.
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                      className="space-y-5"
                    >
                      {[
                        "Real-time performance monitoring",
                        "Daily Generational Reports (DGR)",
                        "Automated performance alerts",
                        "Microsoft Authenticator (2FA) Support"
                      ].map((feature, index) => (
                        <motion.div
                          key={feature}
                          initial={{ opacity: 0, x: -15 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
                          className="flex items-center space-x-4 group"
                        >
                          <div className="w-7 h-7 bg-gradient-to-r from-[#0084CF]/30 via-[#8AC4E6]/40 to-[#8AC4E6]/30 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm border border-[#0084CF]/30">
                            <CheckCircle size={14} className="text-[#0084CF]" />
                          </div>
                          <span className="text-gray-900 font-extrabold group-hover:text-[#0084CF] transition-colors duration-300">{feature}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>
                </section>

                {/* Right Panel - Keep Original Size */}
                <section className="w-full md:w-3/5 p-6 flex flex-col justify-center relative">
                  <div className="w-full max-w-md mx-auto">
                    {mfaRequired ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-6"
                      >
                        <div className="text-center">
                          <div className="inline-flex p-3.5 bg-gradient-to-br from-[#0084CF]/20 via-[#3EA3DA]/20 to-[#0084CF]/30 rounded-2xl border border-[#0084CF]/30 mb-3 shadow-md">
                            <Smartphone size={34} className="text-[#0084CF]" />
                          </div>
                          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                            Two-Factor Authentication
                          </h1>
                          <p className="text-sm font-bold text-gray-700 mt-2">
                            {useBackupCode 
                              ? "Enter an emergency 8-character recovery code" 
                              : <>Enter the 6-digit code from <b>Microsoft Authenticator</b> for <b>{credentials.username}</b></>
                            }
                          </p>
                        </div>

                        {mfaError && (
                          <div className="bg-red-50/95 border border-red-200 rounded-xl p-3.5 text-center">
                            <p className="text-sm text-red-700 font-extrabold">{mfaError}</p>
                          </div>
                        )}

                        <form onSubmit={handleVerifyMFA} className="space-y-5">
                          <div>
                            <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-2 text-center">
                              {useBackupCode ? "Recovery Backup Code" : "6-Digit Authenticator Code"}
                            </label>
                            <input
                              type="text"
                              autoFocus
                              maxLength={useBackupCode ? 10 : 6}
                              placeholder={useBackupCode ? "ABCD-1234" : "000000"}
                              value={mfaCode}
                              onChange={(e) => setMfaCode(e.target.value)}
                              className="w-full text-center text-3xl font-black tracking-[0.35em] p-3.5 bg-white border-2 border-[#0084CF]/40 rounded-xl focus:outline-none focus:border-[#0084CF] focus:ring-4 focus:ring-[#0084CF]/20 shadow-lg text-gray-900 placeholder:text-gray-300"
                              disabled={mfaLoading}
                            />
                          </div>

                          <motion.button
                            type="submit"
                            disabled={mfaLoading || !mfaCode.trim()}
                            whileHover={{ scale: mfaLoading ? 1 : 1.02 }}
                            whileTap={{ scale: mfaLoading ? 1 : 0.98 }}
                            className="w-full py-3.5 rounded-xl font-bold uppercase tracking-wide professional-button text-white shadow-[#0084CF]/40 hover:shadow-2xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {mfaLoading ? (
                              <div className="flex items-center gap-3">
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Verifying Code...</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Shield size={18} />
                                <span>Verify & Sign In</span>
                              </div>
                            )}
                          </motion.button>

                          <div className="flex items-center justify-between pt-2 text-xs font-extrabold">
                            <button
                              type="button"
                              onClick={() => {
                                setUseBackupCode(!useBackupCode);
                                setMfaCode("");
                                setMfaError("");
                              }}
                              className="text-[#0084CF] hover:underline"
                            >
                              {useBackupCode ? "Use Microsoft Authenticator" : "Use Backup Code"}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setMfaRequired(false);
                                setMfaToken("");
                                setMfaCode("");
                                setMfaError("");
                              }}
                              className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
                            >
                              <ArrowLeft size={14} /> Back to Login
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    ) : (
                      <>
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1, duration: 0.5 }}
                          className="mb-6 text-center"
                        >
                          <h1 className="text-4xl font-bold leading-tight pb-1 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent mb-2">
                            User Login
                          </h1>
                          <p className="text-gray-900 font-extrabold">
                            Sign in to access your renewable energy dashboard
                          </p>
                        </motion.div>

                        <form onSubmit={handleLogin} className="space-y-4">
                      {/* Company Code and Site Selection inputs removed */}

                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25, duration: 0.5 }}
                      >
                        <label className="block text-sm font-extrabold mb-2 text-gray-900 flex items-center gap-2">
                          <User size={16} className="text-[#0084CF]" />
                          Username
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Enter your username"
                            className="w-full p-3 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all duration-300 font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-black placeholder-black/70"
                            style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.9)', fontSize: 'calc(1rem + 2px)' }}
                            value={credentials.username}
                            onChange={(e) => handleInputChange('username', e.target.value)}
                            disabled={isLoading}
                          />
                        </div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <label className="block text-sm font-extrabold mb-2 text-gray-900 flex items-center gap-2">
                          <Lock size={16} className="text-[#0084CF]" />
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            className="w-full p-3 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all duration-300 font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-black placeholder-black/70"
                            style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.9)', fontSize: 'calc(1rem + 2px)' }}
                            value={credentials.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            disabled={isLoading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#0084CF] transition-colors duration-200 disabled:cursor-not-allowed disabled:hover:text-gray-600"
                            disabled={isLoading}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </motion.div>

                      {/* Enhanced Remember Me Option */}
                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35, duration: 0.5 }}
                        className="flex items-center gap-3"
                      >
                        <input
                          type="checkbox"
                          id="remember-me"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="remember-checkbox"
                        />
                        <label
                          htmlFor="remember-me"
                          className="text-sm font-extrabold text-gray-900 cursor-pointer select-none"
                        >
                          Remember my credentials
                        </label>
                      </motion.div>

                      {error && (
                        <div className="bg-red-50/95 backdrop-blur-sm border border-red-200 rounded-xl p-4">
                          <p className="text-sm text-red-700 font-extrabold text-center flex items-center justify-center gap-3">
                            <span className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">!</span>
                            </span>
                            {error}
                          </p>
                        </div>
                      )}

                      <motion.button
                        type="submit"
                        disabled={isLoading || !credentials.password || !credentials.username}
                        whileHover={{ scale: isLoading ? 1 : 1.02 }}
                        whileTap={{ scale: isLoading ? 1 : 0.98 }}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className={`w-full py-3 mt-6 rounded-xl font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center shadow-xl ${isLoading || !credentials.password || !credentials.username
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                          : "professional-button text-white shadow-[#0084CF]/40 hover:shadow-[#0084CF]/60 hover:shadow-2xl"
                          }`}
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-4">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Authenticating...</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <Lock size={18} />
                            <span>Login</span>
                          </div>
                        )}
                      </motion.button>

                      <div className="flex items-center justify-center gap-1.5 pt-1 text-xs text-gray-600 font-bold">
                        <Shield size={13} className="text-[#0084CF]" />
                        <span>Secured with Microsoft Authenticator 2FA</span>
                      </div>

                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.42, duration: 0.5 }}
                        className="text-center mt-6 space-y-2"
                      >
                        <p className="text-sm text-gray-900 font-extrabold">
                          Don't have an account?{" "}
                          <Link to="/signup" className="text-[#0084CF] hover:text-[#0066A3] transition-colors font-extrabold">
                            Create one here
                          </Link>
                        </p>
                        <p className="text-sm text-gray-900 font-extrabold">
                          <Link to="/forgot-password" className="text-[#0084CF] hover:text-[#0066A3] transition-colors font-extrabold">
                            Forgot Password?
                          </Link>
                        </p>
                      </motion.div>
                    </form>
                      </>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </motion.div >
        </main >
      </div >
    </div>
  );
};

export default BestLoginPage;
